import ReportType from '@/Enums/Looker/ReportType.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import type SnowflakeDeliveryReportType from '@/Enums/Snowflake/DeliveryReportType.enum';
import { IBaseDataReport } from '@/Interfaces/ReportingDashboard/Beeswax/Report.interface';
import { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { BEESWAX_MAX_RETRIES } from '@/constants/beeswax/asyncReport';
import { getEffectiveFilters } from '@/helpers/reportingDashboard/filters';
import { RootState } from '@/store';
import {
  selectReportingDashboardCurrentApiTabClients,
  selectReportingDashboardCurrentApiTabWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import { fetchLookerReport } from '../beeswaxReports';
import { getBeeswaxReportingFilter } from './filters';

export const lookerReportRequestHandler =
  <TReport = IBaseDataReport>(
    reportType: ReportType | SnowflakeDeliveryReportType,
    fallbackValues: TReport | IBaseDataReport = {
      report: [],
    },
    options: {
      url?: string;
    } = {},
  ): AsyncThunkPayloadCreator<TReport, IAbortSignalThunkArg> =>
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      me: { role },
    } = state;

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);

    const filters = getEffectiveFilters(state, ApiTabIndex.Omnichannel);

    const appliedFilter = getBeeswaxReportingFilter(filters, role!, clients, whitelabels);
    if (!appliedFilter.advertiserIds?.length && !appliedFilter.campaignIds?.length) {
      return { ...fallbackValues, filters } as TReport;
    }

    const { url } = options;

    const reportResponseData = await fetchLookerReport({
      signal: payload?.signal,
      url,
      body: {
        reportType,
        filters: appliedFilter,
      },
      maxRetries: BEESWAX_MAX_RETRIES,
    });

    return { ...(reportResponseData ?? fallbackValues), filters };
  };
